// [ai] The user's layout edits (spec: resize / hide / reorder persisted to user.json, reset to the AI layout) as pure functions
// over the resolved view the server answered: each returns the next user.json document the app PUTs whole (api/views.ts), or
// null when the edit changes nothing. The server validates again (app/lib/views.check_user); view.json is never touched.
import type { UserLayout } from "./types";
export type { UserLayout };
export type LayoutView = { layout?: { cols?: number }; widgets: { id: string; w?: number; h?: number; minW?: number; minH?: number }[]; hidden: { id: string; title: string }[]; user: UserLayout | null };
const MAX_H = 12;
const base = (v: LayoutView): UserLayout => ({ version: 1, ...(v.user?.order ? { order: [...v.user.order] } : {}), widgets: { ...(v.user?.widgets ?? {}) } });
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Swap a visible widget with its neighbour (dir −1 = earlier, +1 = later); the order written is the whole visible order. */
export function move(v: LayoutView, id: string, dir: -1 | 1): UserLayout | null {
  const order = v.widgets.map(w => w.id); const i = order.indexOf(id); const j = i + dir;
  if (i < 0 || j < 0 || j >= order.length) return null;
  [order[i], order[j]] = [order[j]!, order[i]!];
  return { ...base(v), order };
}
/** Grow or shrink by whole cells; clamped to the widget's minW/minH (or 1) and the grid's columns (height to 12 rows). */
export function resize(v: LayoutView, id: string, dw: number, dh: number): UserLayout | null {
  const w = v.widgets.find(x => x.id === id); if (!w) return null;
  const cols = Math.max(1, v.layout?.cols ?? 12);
  const nw = clamp((w.w ?? 4) + dw, w.minW ?? 1, cols), nh = clamp((w.h ?? 2) + dh, w.minH ?? 1, MAX_H);
  if (nw === (w.w ?? 4) && nh === (w.h ?? 2)) return null;
  const u = base(v); u.widgets = { ...u.widgets, [id]: { ...u.widgets[id], w: nw, h: nh } }; return u;
}
export function hide(v: LayoutView, id: string): UserLayout | null {
  if (!v.widgets.some(w => w.id === id)) return null;
  const u = base(v); u.widgets = { ...u.widgets, [id]: { ...u.widgets[id], hidden: true } }; return u;
}
export function show(v: LayoutView, id: string): UserLayout | null {
  if (!v.hidden.some(h => h.id === id)) return null;
  const u = base(v); const { hidden: _h, ...rest } = u.widgets[id] ?? {}; const widgets = { ...u.widgets }; void _h;
  if (Object.keys(rest).length) widgets[id] = rest; else delete widgets[id];
  u.widgets = widgets; return u;
}
